import { useEffect, useState, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { TrendingUp, TrendingDown, RefreshCw, Plus, X, Settings, BarChart3, Activity, Target, Award, Zap, Shield, GitBranch, Layers, History, Grid3X3, Bell, BellRing, Volume2, VolumeX, Check, Trash2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default stocks
const DEFAULT_STOCKS = ["NVDA", "MSFT", "GOOGL", "MU", "SNDK"];
const REFRESH_INTERVAL = 300000; // 5 minutes

// Utility functions
const formatMarketCap = (value) => {
  if (!value) return "N/A";
  if (value >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
  if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
  return `$${value.toLocaleString()}`;
};

const formatNumber = (value, decimals = 2) => {
  if (value === null || value === undefined) return "N/A";
  return value.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

const formatPercent = (value) => {
  if (value === null || value === undefined) return "N/A";
  return `${(value * 100).toFixed(1)}%`;
};

// Stock Card Component
const StockCard = ({ stock }) => {
  const isPositive = stock.change >= 0;
  const changeClass = isPositive ? "positive" : "negative";
  const arrow = isPositive ? "↑" : "↓";
  const changeSymbol = isPositive ? "+" : "";

  return (
    <div className="stock-card" data-testid={`stock-card-${stock.symbol}`}>
      <div className="stock-card-header">
        <div>
          <h3 className="stock-symbol">{stock.symbol}</h3>
          <p className="stock-name">{stock.name}</p>
        </div>
        <div className="stock-price-container">
          <p className="stock-price">${formatNumber(stock.price)}</p>
          <div className={`stock-change ${changeClass}`}>
            {arrow} {changeSymbol}{formatNumber(stock.change)} ({changeSymbol}{formatNumber(stock.change_percent)}%)
          </div>
        </div>
      </div>
      <div className="stock-metrics">
        <div className="metric">
          <p className="metric-label">Market Cap</p>
          <p className="metric-value">{formatMarketCap(stock.market_cap)}</p>
        </div>
        <div className="metric">
          <p className="metric-label">P/E Ratio</p>
          <p className="metric-value">{stock.pe_ratio ? formatNumber(stock.pe_ratio) : "N/A"}</p>
        </div>
        <div className="metric">
          <p className="metric-label">EPS</p>
          <p className="metric-value">{stock.eps ? `$${formatNumber(stock.eps)}` : "N/A"}</p>
        </div>
        <div className="metric">
          <p className="metric-label">Volume</p>
          <p className="metric-value">{stock.volume ? stock.volume.toLocaleString() : "N/A"}</p>
        </div>
        <div className="metric">
          <p className="metric-label">52W High</p>
          <p className="metric-value">{stock.high_52w ? `$${formatNumber(stock.high_52w)}` : "N/A"}</p>
        </div>
        <div className="metric">
          <p className="metric-label">52W Low</p>
          <p className="metric-value">{stock.low_52w ? `$${formatNumber(stock.low_52w)}` : "N/A"}</p>
        </div>
      </div>
    </div>
  );
};

// Options Table Component
const OptionsTable = ({ options }) => {
  if (!options || options.length === 0) {
    return (
      <div className="error-card">
        <p>No options data available. Options may not be available for some stocks or the market may be closed.</p>
      </div>
    );
  }

  return (
    <div className="options-table-container" data-testid="options-table">
      <table className="options-table">
        <thead>
          <tr>
            <th>Symbol</th>
            <th>Expiration</th>
            <th>Strike (+10%)</th>
            <th>Bid Price</th>
            <th>Ask</th>
            <th>Last</th>
            <th>Volume</th>
            <th>OI</th>
            <th>IV</th>
          </tr>
        </thead>
        <tbody>
          {options.map((opt, idx) => (
            <tr key={`${opt.symbol}-${opt.strike}-${idx}`}>
              <td className="symbol-cell">{opt.symbol}</td>
              <td>{opt.expiration}</td>
              <td>${formatNumber(opt.strike)}</td>
              <td className="bid-cell">{opt.bid !== null ? `$${formatNumber(opt.bid)}` : "N/A"}</td>
              <td>{opt.ask !== null ? `$${formatNumber(opt.ask)}` : "N/A"}</td>
              <td>{opt.last_price !== null ? `$${formatNumber(opt.last_price)}` : "N/A"}</td>
              <td>{opt.volume !== null ? opt.volume.toLocaleString() : "N/A"}</td>
              <td>{opt.open_interest !== null ? opt.open_interest.toLocaleString() : "N/A"}</td>
              <td>{formatPercent(opt.implied_volatility)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Options Summary Component
const OptionsSummary = ({ stocks, options }) => {
  const getSummary = (symbol) => {
    const stock = stocks.find(s => s.symbol === symbol);
    const stockOptions = options.filter(o => o.symbol === symbol);
    
    if (!stock || stockOptions.length === 0) return null;
    
    const targetStrike = stock.price * 1.10;
    const firstOption = stockOptions[0];
    
    return {
      symbol,
      currentPrice: stock.price,
      targetStrike,
      nearestStrike: firstOption.strike,
      bidPrice: firstOption.bid
    };
  };

  const summaries = [...new Set(options.map(o => o.symbol))]
    .map(getSummary)
    .filter(s => s !== null);

  if (summaries.length === 0) return null;

  return (
    <>
      <div className="section-header" style={{ marginTop: '32px' }}>
        <span className="section-icon">📋</span>
        <h2>Options Summary</h2>
      </div>
      <div className="options-summary-grid">
        {summaries.map(summary => (
          <div key={summary.symbol} className="option-summary-card" data-testid={`option-summary-${summary.symbol}`}>
            <h4 className="stock-symbol">{summary.symbol}</h4>
            <div className="option-summary-item">
              <p className="option-summary-label">Current Price</p>
              <p className="option-summary-value">${formatNumber(summary.currentPrice)}</p>
            </div>
            <div className="option-summary-item">
              <p className="option-summary-label">Target Strike (+10%)</p>
              <p className="option-summary-value">${formatNumber(summary.targetStrike)}</p>
            </div>
            <div className="option-summary-item">
              <p className="option-summary-label">Nearest Strike</p>
              <p className="option-summary-value">${formatNumber(summary.nearestStrike)}</p>
            </div>
            <div className="option-summary-item">
              <p className="option-summary-label">Bid Price</p>
              <p className="option-summary-value highlight">
                {summary.bidPrice !== null ? `$${formatNumber(summary.bidPrice)}` : "N/A"}
              </p>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

// Covered Call Screener Component
const CoveredCallScreener = ({ opportunities }) => {
  if (!opportunities || opportunities.length === 0) {
    return (
      <div className="error-card">
        <p>No covered call opportunities available at this time.</p>
      </div>
    );
  }

  // Get top 3 opportunities with highest scores
  const topOpportunities = opportunities.slice(0, 3);
  
  const getScoreClass = (score) => {
    if (score >= 15) return "score-excellent";
    if (score >= 10) return "score-good";
    return "score-fair";
  };

  const getScoreLabel = (score) => {
    if (score >= 15) return "Excellent";
    if (score >= 10) return "Good";
    return "Fair";
  };

  return (
    <>
      {/* Top Picks */}
      <div className="screener-top-picks" data-testid="screener-top-picks">
        <h3 className="screener-subtitle">
          <Award size={18} style={{ color: '#f59e0b', marginRight: '8px' }} />
          Top Picks
        </h3>
        <div className="top-picks-grid">
          {topOpportunities.map((opp, idx) => (
            <div key={`top-${opp.symbol}-${opp.strike}-${idx}`} className={`top-pick-card rank-${idx + 1}`}>
              <div className="top-pick-rank">#{idx + 1}</div>
              <div className="top-pick-header">
                <span className="top-pick-symbol">{opp.symbol}</span>
                <span className={`score-badge ${getScoreClass(opp.score)}`}>
                  {getScoreLabel(opp.score)}
                </span>
              </div>
              <div className="top-pick-strike">${formatNumber(opp.strike)} Strike</div>
              <div className="top-pick-expiry">{opp.expiration} ({opp.days_to_expiry}d)</div>
              <div className="top-pick-metrics">
                <div className="top-pick-metric">
                  <span className="metric-label">Premium</span>
                  <span className="metric-value highlight">${formatNumber(opp.bid)}</span>
                </div>
                <div className="top-pick-metric">
                  <span className="metric-label">Yield</span>
                  <span className="metric-value">{opp.premium_percent}%</span>
                </div>
                <div className="top-pick-metric">
                  <span className="metric-label">Annualized</span>
                  <span className="metric-value highlight">{opp.annualized_return}%</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Full Table */}
      <div className="screener-table-container" data-testid="screener-table">
        <table className="options-table screener-table">
          <thead>
            <tr>
              <th>Symbol</th>
              <th>Stock Price</th>
              <th>Strike</th>
              <th>Expiration</th>
              <th>Premium (Bid)</th>
              <th>Yield %</th>
              <th>Annualized %</th>
              <th>Upside %</th>
              <th>Breakeven</th>
              <th>Score</th>
            </tr>
          </thead>
          <tbody>
            {opportunities.map((opp, idx) => (
              <tr key={`${opp.symbol}-${opp.strike}-${idx}`} className={idx < 3 ? 'highlighted-row' : ''}>
                <td className="symbol-cell">{opp.symbol}</td>
                <td>${formatNumber(opp.stock_price)}</td>
                <td>${formatNumber(opp.strike)}</td>
                <td>{opp.expiration} <span className="days-badge">({opp.days_to_expiry}d)</span></td>
                <td className="bid-cell">${formatNumber(opp.bid)}</td>
                <td>{opp.premium_percent}%</td>
                <td className="annualized-cell">{opp.annualized_return}%</td>
                <td className="upside-cell">+{opp.upside_potential}%</td>
                <td>${formatNumber(opp.breakeven)}</td>
                <td>
                  <span className={`score-badge ${getScoreClass(opp.score)}`}>
                    {opp.score}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

// Cash Secured Puts Screener Component
const CashSecuredPutsScreener = ({ opportunities }) => {
  if (!opportunities || opportunities.length === 0) {
    return (
      <div className="error-card">
        <p>No cash-secured put opportunities available at this time.</p>
      </div>
    );
  }

  const topOpportunities = opportunities.slice(0, 3);
  
  const getScoreClass = (score) => {
    if (score >= 15) return "score-excellent";
    if (score >= 10) return "score-good";
    return "score-fair";
  };

  const getScoreLabel = (score) => {
    if (score >= 15) return "Excellent";
    if (score >= 10) return "Good";
    return "Fair";
  };

  return (
    <>
      {/* Top Picks */}
      <div className="screener-top-picks" data-testid="puts-top-picks">
        <h3 className="screener-subtitle">
          <Award size={18} style={{ color: '#8b5cf6', marginRight: '8px' }} />
          Top Put Opportunities
        </h3>
        <div className="top-picks-grid">
          {topOpportunities.map((opp, idx) => (
            <div key={`put-${opp.symbol}-${opp.strike}-${idx}`} className={`top-pick-card put-card rank-${idx + 1}`}>
              <div className="top-pick-rank put-rank">#{idx + 1}</div>
              <div className="top-pick-header">
                <span className="top-pick-symbol">{opp.symbol}</span>
                <span className={`score-badge ${getScoreClass(opp.score)}`}>
                  {getScoreLabel(opp.score)}
                </span>
              </div>
              <div className="top-pick-strike">${formatNumber(opp.strike)} Strike</div>
              <div className="top-pick-expiry">{opp.expiration} ({opp.days_to_expiry}d)</div>
              <div className="top-pick-metrics">
                <div className="top-pick-metric">
                  <span className="metric-label">Premium</span>
                  <span className="metric-value highlight">${formatNumber(opp.bid)}</span>
                </div>
                <div className="top-pick-metric">
                  <span className="metric-label">Protection</span>
                  <span className="metric-value">{opp.downside_protection}%</span>
                </div>
                <div className="top-pick-metric">
                  <span className="metric-label">Eff. Buy</span>
                  <span className="metric-value highlight">${formatNumber(opp.effective_buy_price)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Full Table */}
      <div className="screener-table-container puts-table" data-testid="puts-table">
        <table className="options-table screener-table">
          <thead>
            <tr>
              <th>Symbol</th>
              <th>Stock Price</th>
              <th>Strike</th>
              <th>Expiration</th>
              <th>Premium (Bid)</th>
              <th>Yield %</th>
              <th>Annualized %</th>
              <th>Protection %</th>
              <th>Eff. Buy Price</th>
              <th>Score</th>
            </tr>
          </thead>
          <tbody>
            {opportunities.map((opp, idx) => (
              <tr key={`put-${opp.symbol}-${opp.strike}-${idx}`} className={idx < 3 ? 'highlighted-row put-highlight' : ''}>
                <td className="symbol-cell">{opp.symbol}</td>
                <td>${formatNumber(opp.stock_price)}</td>
                <td>${formatNumber(opp.strike)}</td>
                <td>{opp.expiration} <span className="days-badge">({opp.days_to_expiry}d)</span></td>
                <td className="bid-cell">${formatNumber(opp.bid)}</td>
                <td>{opp.premium_percent}%</td>
                <td className="annualized-cell">{opp.annualized_return}%</td>
                <td className="protection-cell">{opp.downside_protection}%</td>
                <td className="effective-buy-cell">${formatNumber(opp.effective_buy_price)}</td>
                <td>
                  <span className={`score-badge ${getScoreClass(opp.score)}`}>
                    {opp.score}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

// Spreads Screener Component
const SpreadsScreener = ({ spreads }) => {
  if (!spreads || spreads.length === 0) {
    return (
      <div className="error-card">
        <p>No spread opportunities available at this time.</p>
      </div>
    );
  }

  const getSpreadTypeLabel = (type) => {
    switch (type) {
      case 'bull_call': return 'Bull Call';
      case 'bear_put': return 'Bear Put';
      case 'bull_put_credit': return 'Bull Put Credit';
      case 'bear_call_credit': return 'Bear Call Credit';
      default: return type;
    }
  };

  const getSpreadTypeClass = (type) => {
    if (type.includes('bull')) return 'spread-bullish';
    if (type.includes('bear')) return 'spread-bearish';
    return '';
  };

  const isCredit = (spread) => spread.net_credit !== null;

  const topSpreads = spreads.slice(0, 3);

  return (
    <>
      {/* Top Spreads */}
      <div className="screener-top-picks" data-testid="spreads-top-picks">
        <h3 className="screener-subtitle">
          <Award size={18} style={{ color: '#06b6d4', marginRight: '8px' }} />
          Top Spread Strategies
        </h3>
        <div className="top-picks-grid">
          {topSpreads.map((spread, idx) => (
            <div key={`spread-${spread.symbol}-${spread.long_strike}-${idx}`} className={`top-pick-card spread-card rank-${idx + 1}`}>
              <div className="top-pick-rank spread-rank">#{idx + 1}</div>
              <div className="top-pick-header">
                <span className="top-pick-symbol">{spread.symbol}</span>
                <span className={`spread-type-badge ${getSpreadTypeClass(spread.spread_type)}`}>
                  {getSpreadTypeLabel(spread.spread_type)}
                </span>
              </div>
              <div className="top-pick-strike">
                ${formatNumber(spread.long_strike)} / ${formatNumber(spread.short_strike)}
              </div>
              <div className="top-pick-expiry">{spread.expiration} ({spread.days_to_expiry}d)</div>
              <div className="top-pick-metrics spread-metrics">
                <div className="top-pick-metric">
                  <span className="metric-label">{isCredit(spread) ? 'Credit' : 'Debit'}</span>
                  <span className="metric-value highlight">
                    ${formatNumber(isCredit(spread) ? spread.net_credit : spread.net_debit)}
                  </span>
                </div>
                <div className="top-pick-metric">
                  <span className="metric-label">Max Profit</span>
                  <span className="metric-value highlight">${formatNumber(spread.max_profit)}</span>
                </div>
                <div className="top-pick-metric">
                  <span className="metric-label">R:R</span>
                  <span className="metric-value">{spread.risk_reward_ratio}:1</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Full Table */}
      <div className="screener-table-container spreads-table" data-testid="spreads-table">
        <table className="options-table screener-table">
          <thead>
            <tr>
              <th>Symbol</th>
              <th>Strategy</th>
              <th>Strikes</th>
              <th>Expiration</th>
              <th>Debit/Credit</th>
              <th>Max Profit</th>
              <th>Max Loss</th>
              <th>Breakeven</th>
              <th>R:R Ratio</th>
              <th>Score</th>
            </tr>
          </thead>
          <tbody>
            {spreads.map((spread, idx) => (
              <tr key={`spread-row-${spread.symbol}-${spread.long_strike}-${idx}`} className={idx < 3 ? 'highlighted-row spread-highlight' : ''}>
                <td className="symbol-cell">{spread.symbol}</td>
                <td>
                  <span className={`spread-type-badge small ${getSpreadTypeClass(spread.spread_type)}`}>
                    {getSpreadTypeLabel(spread.spread_type)}
                  </span>
                </td>
                <td>${formatNumber(spread.long_strike)} / ${formatNumber(spread.short_strike)}</td>
                <td>{spread.expiration} <span className="days-badge">({spread.days_to_expiry}d)</span></td>
                <td className={isCredit(spread) ? 'credit-cell' : 'debit-cell'}>
                  {isCredit(spread) ? '+' : '-'}${formatNumber(isCredit(spread) ? spread.net_credit : spread.net_debit)}
                </td>
                <td className="profit-cell">${formatNumber(spread.max_profit)}</td>
                <td className="loss-cell">${formatNumber(spread.max_loss)}</td>
                <td>${formatNumber(spread.breakeven)}</td>
                <td>{spread.risk_reward_ratio}:1</td>
                <td>
                  <span className="score-badge score-good">{spread.score}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

// Iron Condor Component
const IronCondorScreener = ({ condors }) => {
  if (!condors || condors.length === 0) {
    return (
      <div className="error-card">
        <p>No iron condor opportunities available at this time.</p>
      </div>
    );
  }

  const topCondors = condors.slice(0, 3);

  return (
    <>
      <div className="screener-top-picks" data-testid="condor-top-picks">
        <h3 className="screener-subtitle">
          <Award size={18} style={{ color: '#ec4899', marginRight: '8px' }} />
          Top Iron Condors
        </h3>
        <div className="top-picks-grid">
          {topCondors.map((c, idx) => (
            <div key={`condor-${c.symbol}-${idx}`} className={`top-pick-card condor-card rank-${idx + 1}`}>
              <div className="top-pick-rank condor-rank">#{idx + 1}</div>
              <div className="top-pick-header">
                <span className="top-pick-symbol">{c.symbol}</span>
                <span className="spread-type-badge spread-neutral">Iron Condor</span>
              </div>
              <div className="condor-strikes">
                <span className="strike-label">Put: ${formatNumber(c.long_put_strike)}/{formatNumber(c.short_put_strike)}</span>
                <span className="strike-label">Call: ${formatNumber(c.short_call_strike)}/{formatNumber(c.long_call_strike)}</span>
              </div>
              <div className="top-pick-expiry">{c.expiration} ({c.days_to_expiry}d)</div>
              <div className="top-pick-metrics">
                <div className="top-pick-metric">
                  <span className="metric-label">Credit</span>
                  <span className="metric-value highlight">${formatNumber(c.net_credit)}</span>
                </div>
                <div className="top-pick-metric">
                  <span className="metric-label">Profit Zone</span>
                  <span className="metric-value">{c.profit_range}%</span>
                </div>
                <div className="top-pick-metric">
                  <span className="metric-label">R:R</span>
                  <span className="metric-value">{c.risk_reward_ratio}:1</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="screener-table-container condor-table" data-testid="condor-table">
        <table className="options-table screener-table">
          <thead>
            <tr>
              <th>Symbol</th>
              <th>Put Spread</th>
              <th>Call Spread</th>
              <th>Expiration</th>
              <th>Credit</th>
              <th>Max Profit</th>
              <th>Max Loss</th>
              <th>Breakevens</th>
              <th>Profit Zone</th>
              <th>Score</th>
            </tr>
          </thead>
          <tbody>
            {condors.map((c, idx) => (
              <tr key={`condor-row-${c.symbol}-${idx}`} className={idx < 3 ? 'highlighted-row condor-highlight' : ''}>
                <td className="symbol-cell">{c.symbol}</td>
                <td>${formatNumber(c.long_put_strike)}/${formatNumber(c.short_put_strike)}</td>
                <td>${formatNumber(c.short_call_strike)}/${formatNumber(c.long_call_strike)}</td>
                <td>{c.expiration} <span className="days-badge">({c.days_to_expiry}d)</span></td>
                <td className="credit-cell">${formatNumber(c.net_credit)}</td>
                <td className="profit-cell">${formatNumber(c.max_profit)}</td>
                <td className="loss-cell">${formatNumber(c.max_loss)}</td>
                <td>${formatNumber(c.breakeven_lower)} - ${formatNumber(c.breakeven_upper)}</td>
                <td className="profit-zone-cell">{c.profit_range}%</td>
                <td><span className="score-badge score-good">{c.score}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

// Butterfly Spread Component
const ButterflyScreener = ({ butterflies }) => {
  if (!butterflies || butterflies.length === 0) {
    return (
      <div className="error-card">
        <p>No butterfly spread opportunities available at this time.</p>
      </div>
    );
  }

  const topButterflies = butterflies.slice(0, 3);

  return (
    <>
      <div className="screener-top-picks" data-testid="butterfly-top-picks">
        <h3 className="screener-subtitle">
          <Award size={18} style={{ color: '#10b981', marginRight: '8px' }} />
          Top Butterfly Spreads
        </h3>
        <div className="top-picks-grid">
          {topButterflies.map((b, idx) => (
            <div key={`butterfly-${b.symbol}-${idx}`} className={`top-pick-card butterfly-card rank-${idx + 1}`}>
              <div className="top-pick-rank butterfly-rank">#{idx + 1}</div>
              <div className="top-pick-header">
                <span className="top-pick-symbol">{b.symbol}</span>
                <span className="spread-type-badge spread-neutral">Butterfly</span>
              </div>
              <div className="top-pick-strike">
                ${formatNumber(b.lower_strike)} / ${formatNumber(b.middle_strike)} / ${formatNumber(b.upper_strike)}
              </div>
              <div className="top-pick-expiry">{b.expiration} ({b.days_to_expiry}d)</div>
              <div className="top-pick-metrics">
                <div className="top-pick-metric">
                  <span className="metric-label">Debit</span>
                  <span className="metric-value">${formatNumber(b.net_debit)}</span>
                </div>
                <div className="top-pick-metric">
                  <span className="metric-label">Max Profit</span>
                  <span className="metric-value highlight">${formatNumber(b.max_profit)}</span>
                </div>
                <div className="top-pick-metric">
                  <span className="metric-label">R:R</span>
                  <span className="metric-value highlight">{b.risk_reward_ratio}:1</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="screener-table-container butterfly-table" data-testid="butterfly-table">
        <table className="options-table screener-table">
          <thead>
            <tr>
              <th>Symbol</th>
              <th>Type</th>
              <th>Lower</th>
              <th>Middle</th>
              <th>Upper</th>
              <th>Expiration</th>
              <th>Debit</th>
              <th>Max Profit</th>
              <th>R:R</th>
              <th>Score</th>
            </tr>
          </thead>
          <tbody>
            {butterflies.map((b, idx) => (
              <tr key={`butterfly-row-${b.symbol}-${idx}`} className={idx < 3 ? 'highlighted-row butterfly-highlight' : ''}>
                <td className="symbol-cell">{b.symbol}</td>
                <td><span className="spread-type-badge spread-neutral small">Call</span></td>
                <td>${formatNumber(b.lower_strike)}</td>
                <td>${formatNumber(b.middle_strike)}</td>
                <td>${formatNumber(b.upper_strike)}</td>
                <td>{b.expiration} <span className="days-badge">({b.days_to_expiry}d)</span></td>
                <td className="debit-cell">${formatNumber(b.net_debit)}</td>
                <td className="profit-cell">${formatNumber(b.max_profit)}</td>
                <td>{b.risk_reward_ratio}:1</td>
                <td><span className="score-badge score-good">{b.score}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

// Options Chain Component
const OptionsChainView = ({ chain, selectedSymbol }) => {
  const [selectedExpiry, setSelectedExpiry] = useState(0);
  
  if (!chain || chain.length === 0) {
    return (
      <div className="error-card">
        <p>No options chain data available.</p>
      </div>
    );
  }

  const currentChain = chain[selectedExpiry] || chain[0];

  const getDeltaClass = (delta) => {
    const absDelta = Math.abs(delta);
    if (absDelta >= 0.7) return 'delta-high';
    if (absDelta >= 0.3) return 'delta-mid';
    return 'delta-low';
  };

  return (
    <div className="options-chain-container" data-testid="options-chain">
      <div className="chain-header">
        <div className="chain-info">
          <span className="chain-symbol">{currentChain.symbol}</span>
          <span className="chain-price">Stock: ${formatNumber(currentChain.stock_price)}</span>
        </div>
        <div className="expiry-selector">
          {chain.map((c, idx) => (
            <button 
              key={c.expiration}
              className={`expiry-btn ${selectedExpiry === idx ? 'active' : ''}`}
              onClick={() => setSelectedExpiry(idx)}
            >
              {c.expiration}
            </button>
          ))}
        </div>
      </div>

      <div className="chain-tables">
        <div className="chain-side calls-side">
          <h4 className="chain-side-title calls-title">CALLS</h4>
          <table className="chain-table">
            <thead>
              <tr>
                <th>Bid</th>
                <th>Ask</th>
                <th>Δ</th>
                <th>Γ</th>
                <th>Θ</th>
                <th>V</th>
                <th>IV%</th>
                <th>OI</th>
              </tr>
            </thead>
            <tbody>
              {currentChain.calls.slice(0, 15).map((opt, idx) => (
                <tr key={`call-${opt.strike}-${idx}`} className={opt.in_the_money ? 'itm-row' : ''}>
                  <td className="bid-cell">{opt.bid ? `$${formatNumber(opt.bid)}` : '-'}</td>
                  <td>{opt.ask ? `$${formatNumber(opt.ask)}` : '-'}</td>
                  <td className={getDeltaClass(opt.delta)}>{opt.delta}</td>
                  <td>{opt.gamma}</td>
                  <td className="theta-cell">{opt.theta}</td>
                  <td>{opt.vega}</td>
                  <td>{opt.implied_volatility}%</td>
                  <td>{opt.open_interest?.toLocaleString() || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="strike-column">
          <h4>Strike</h4>
          {currentChain.calls.slice(0, 15).map((opt, idx) => (
            <div key={`strike-${opt.strike}-${idx}`} className={`strike-cell ${opt.strike <= currentChain.stock_price ? 'itm-strike' : ''}`}>
              ${formatNumber(opt.strike)}
            </div>
          ))}
        </div>

        <div className="chain-side puts-side">
          <h4 className="chain-side-title puts-title">PUTS</h4>
          <table className="chain-table">
            <thead>
              <tr>
                <th>Bid</th>
                <th>Ask</th>
                <th>Δ</th>
                <th>Γ</th>
                <th>Θ</th>
                <th>V</th>
                <th>IV%</th>
                <th>OI</th>
              </tr>
            </thead>
            <tbody>
              {currentChain.puts.slice(0, 15).map((opt, idx) => (
                <tr key={`put-${opt.strike}-${idx}`} className={opt.in_the_money ? 'itm-row' : ''}>
                  <td className="bid-cell">{opt.bid ? `$${formatNumber(opt.bid)}` : '-'}</td>
                  <td>{opt.ask ? `$${formatNumber(opt.ask)}` : '-'}</td>
                  <td className={getDeltaClass(opt.delta)}>{opt.delta}</td>
                  <td>{opt.gamma}</td>
                  <td className="theta-cell">{opt.theta}</td>
                  <td>{opt.vega}</td>
                  <td>{opt.implied_volatility}%</td>
                  <td>{opt.open_interest?.toLocaleString() || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="greeks-legend">
        <span><strong>Δ</strong> Delta</span>
        <span><strong>Γ</strong> Gamma</span>
        <span><strong>Θ</strong> Theta (daily)</span>
        <span><strong>V</strong> Vega</span>
        <span className="itm-indicator">ITM</span>
      </div>
    </div>
  );
};

// Backtest Component
const BacktestResults = ({ backtests }) => {
  const [selectedBacktest, setSelectedBacktest] = useState(0);

  if (!backtests || backtests.length === 0) {
    return (
      <div className="error-card">
        <p>No backtest data available.</p>
      </div>
    );
  }

  const bt = backtests[selectedBacktest];

  return (
    <div className="backtest-container" data-testid="backtest-results">
      <div className="backtest-tabs">
        {backtests.map((b, idx) => (
          <button 
            key={b.symbol}
            className={`backtest-tab ${selectedBacktest === idx ? 'active' : ''}`}
            onClick={() => setSelectedBacktest(idx)}
          >
            {b.symbol}
          </button>
        ))}
      </div>

      <div className="backtest-summary">
        <div className="backtest-stat">
          <span className="stat-label">Total Trades</span>
          <span className="stat-value">{bt.total_trades}</span>
        </div>
        <div className="backtest-stat">
          <span className="stat-label">Win Rate</span>
          <span className="stat-value highlight">{bt.win_rate}%</span>
        </div>
        <div className="backtest-stat">
          <span className="stat-label">Total P&L</span>
          <span className={`stat-value ${bt.total_pnl >= 0 ? 'positive' : 'negative'}`}>
            ${formatNumber(bt.total_pnl)}
          </span>
        </div>
        <div className="backtest-stat">
          <span className="stat-label">Avg Win</span>
          <span className="stat-value positive">${formatNumber(bt.avg_profit)}</span>
        </div>
        <div className="backtest-stat">
          <span className="stat-label">Avg Loss</span>
          <span className="stat-value negative">${formatNumber(bt.avg_loss)}</span>
        </div>
        <div className="backtest-stat">
          <span className="stat-label">Max Win</span>
          <span className="stat-value">${formatNumber(bt.max_win)}</span>
        </div>
      </div>

      <div className="backtest-chart">
        <h4>P&L History</h4>
        <div className="pnl-bars">
          {bt.trades.map((trade, idx) => (
            <div key={idx} className="pnl-bar-container">
              <div 
                className={`pnl-bar ${trade.pnl >= 0 ? 'profit-bar' : 'loss-bar'}`}
                style={{ height: `${Math.min(Math.abs(trade.pnl_percent) * 5, 100)}%` }}
                title={`${trade.entry_date}: $${trade.pnl} (${trade.pnl_percent}%)`}
              />
              <span className="pnl-date">{trade.entry_date.slice(5)}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="backtest-trades-table">
        <table className="options-table">
          <thead>
            <tr>
              <th>Entry</th>
              <th>Exit</th>
              <th>Entry $</th>
              <th>Exit $</th>
              <th>P&L</th>
              <th>P&L %</th>
              <th>Outcome</th>
            </tr>
          </thead>
          <tbody>
            {bt.trades.map((trade, idx) => (
              <tr key={idx}>
                <td>{trade.entry_date}</td>
                <td>{trade.exit_date}</td>
                <td>${formatNumber(trade.entry_price)}</td>
                <td>${formatNumber(trade.exit_price)}</td>
                <td className={trade.pnl >= 0 ? 'profit-cell' : 'loss-cell'}>
                  ${formatNumber(trade.pnl)}
                </td>
                <td className={trade.pnl_percent >= 0 ? 'profit-cell' : 'loss-cell'}>
                  {trade.pnl_percent}%
                </td>
                <td>
                  <span className={`outcome-badge ${trade.outcome}`}>
                    {trade.outcome.replace('_', ' ')}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Alerts Panel Component
const AlertsPanel = ({ alerts, onMarkRead, onMarkAllRead, onDelete, onClearAll, settings, onUpdateSettings }) => {
  const [showSettings, setShowSettings] = useState(false);
  const [localSettings, setLocalSettings] = useState(settings);

  useEffect(() => {
    setLocalSettings(settings);
  }, [settings]);

  const getStrategyLabel = (strategy) => {
    const labels = {
      'covered_call': 'Covered Call',
      'cash_secured_put': 'Cash-Secured Put',
      'iron_condor': 'Iron Condor',
      'butterfly': 'Butterfly',
      'bull_call': 'Bull Call Spread',
      'bear_put': 'Bear Put Spread',
      'bull_put_credit': 'Bull Put Credit',
      'spread': 'Spread'
    };
    return labels[strategy] || strategy;
  };

  const getStrategyColor = (strategy) => {
    const colors = {
      'covered_call': '#f59e0b',
      'cash_secured_put': '#8b5cf6',
      'iron_condor': '#ec4899',
      'butterfly': '#10b981',
      'bull_call': '#22c55e',
      'bear_put': '#ef4444',
      'bull_put_credit': '#22c55e',
      'spread': '#06b6d4'
    };
    return colors[strategy] || '#6366f1';
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleSaveSettings = () => {
    onUpdateSettings(localSettings);
    setShowSettings(false);
  };

  return (
    <div className="alerts-panel" data-testid="alerts-panel">
      <div className="alerts-panel-header">
        <div className="alerts-title">
          <BellRing size={20} />
          <span>Real-Time Alerts</span>
          {alerts.filter(a => !a.read).length > 0 && (
            <span className="alerts-badge">{alerts.filter(a => !a.read).length}</span>
          )}
        </div>
        <div className="alerts-actions">
          <button 
            className="alerts-action-btn"
            onClick={() => setShowSettings(!showSettings)}
            title="Alert Settings"
          >
            <Settings size={16} />
          </button>
          <button 
            className="alerts-action-btn"
            onClick={onMarkAllRead}
            title="Mark All Read"
          >
            <Check size={16} />
          </button>
          <button 
            className="alerts-action-btn danger"
            onClick={onClearAll}
            title="Clear All"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      {showSettings && (
        <div className="alerts-settings" data-testid="alerts-settings">
          <h4>Alert Thresholds</h4>
          <div className="threshold-grid">
            <div className="threshold-item">
              <label>Covered Call</label>
              <input 
                type="number" 
                value={localSettings.covered_call_threshold || 40}
                onChange={(e) => setLocalSettings({...localSettings, covered_call_threshold: parseFloat(e.target.value)})}
              />
            </div>
            <div className="threshold-item">
              <label>Cash-Secured Put</label>
              <input 
                type="number" 
                value={localSettings.cash_secured_put_threshold || 35}
                onChange={(e) => setLocalSettings({...localSettings, cash_secured_put_threshold: parseFloat(e.target.value)})}
              />
            </div>
            <div className="threshold-item">
              <label>Iron Condor</label>
              <input 
                type="number" 
                value={localSettings.iron_condor_threshold || 50}
                onChange={(e) => setLocalSettings({...localSettings, iron_condor_threshold: parseFloat(e.target.value)})}
              />
            </div>
            <div className="threshold-item">
              <label>Butterfly</label>
              <input 
                type="number" 
                value={localSettings.butterfly_threshold || 80}
                onChange={(e) => setLocalSettings({...localSettings, butterfly_threshold: parseFloat(e.target.value)})}
              />
            </div>
          </div>
          <div className="threshold-toggles">
            <label className="toggle-label">
              <input 
                type="checkbox" 
                checked={localSettings.alerts_enabled !== false}
                onChange={(e) => setLocalSettings({...localSettings, alerts_enabled: e.target.checked})}
              />
              <span>Alerts Enabled</span>
            </label>
            <label className="toggle-label">
              <input 
                type="checkbox" 
                checked={localSettings.sound_enabled !== false}
                onChange={(e) => setLocalSettings({...localSettings, sound_enabled: e.target.checked})}
              />
              <span>Sound Enabled</span>
            </label>
          </div>
          <button className="btn btn-primary btn-full" onClick={handleSaveSettings}>
            Save Settings
          </button>
        </div>
      )}

      <div className="alerts-list">
        {alerts.length === 0 ? (
          <div className="no-alerts">
            <Bell size={32} />
            <p>No alerts yet</p>
            <span>High-score opportunities will appear here</span>
          </div>
        ) : (
          alerts.map((alert) => (
            <div 
              key={alert.id} 
              className={`alert-item ${alert.read ? 'read' : 'unread'}`}
              data-testid={`alert-${alert.id}`}
            >
              <div 
                className="alert-indicator" 
                style={{ backgroundColor: getStrategyColor(alert.strategy) }}
              />
              <div className="alert-content">
                <div className="alert-header">
                  <span className="alert-symbol">{alert.symbol}</span>
                  <span 
                    className="alert-strategy"
                    style={{ color: getStrategyColor(alert.strategy) }}
                  >
                    {getStrategyLabel(alert.strategy)}
                  </span>
                </div>
                <div className="alert-score">
                  Score: <strong>{alert.score}</strong>
                </div>
                <div className="alert-details">
                  {alert.details.strike && <span>Strike: ${formatNumber(alert.details.strike)}</span>}
                  {alert.details.premium && <span>Premium: ${formatNumber(alert.details.premium)}</span>}
                  {alert.details.net_credit && <span>Credit: ${formatNumber(alert.details.net_credit)}</span>}
                  {alert.details.max_profit && <span>Max Profit: ${formatNumber(alert.details.max_profit)}</span>}
                  {alert.details.expiration && <span>Exp: {alert.details.expiration}</span>}
                </div>
                <div className="alert-time">{formatTime(alert.triggered_at)}</div>
              </div>
              <div className="alert-actions">
                {!alert.read && (
                  <button 
                    className="alert-btn"
                    onClick={() => onMarkRead(alert.id)}
                    title="Mark as read"
                  >
                    <Check size={14} />
                  </button>
                )}
                <button 
                  className="alert-btn danger"
                  onClick={() => onDelete(alert.id)}
                  title="Delete"
                >
                  <X size={14} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// Loading Component
const Loading = () => (
  <div className="loading-container">
    <div className="loading-spinner"></div>
    <p>Loading market data...</p>
  </div>
);

// Toast Component
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`toast ${type}`}>
      <p style={{ margin: 0, color: type === 'success' ? '#22c55e' : '#ef4444' }}>{message}</p>
    </div>
  );
};

// Main App Component
function App() {
  const [watchlist, setWatchlist] = useState(DEFAULT_STOCKS);
  const [stocks, setStocks] = useState([]);
  const [options, setOptions] = useState([]);
  const [coveredCalls, setCoveredCalls] = useState([]);
  const [cashSecuredPuts, setCashSecuredPuts] = useState([]);
  const [spreads, setSpreads] = useState([]);
  const [ironCondors, setIronCondors] = useState([]);
  const [butterflies, setButterflies] = useState([]);
  const [optionsChain, setOptionsChain] = useState([]);
  const [backtests, setBacktests] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [alertSettings, setAlertSettings] = useState({});
  const [showAlertsPanel, setShowAlertsPanel] = useState(false);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [newStock, setNewStock] = useState("");
  const [selectedRemove, setSelectedRemove] = useState("");
  const [toasts, setToasts] = useState([]);
  const [chainSymbol, setChainSymbol] = useState("NVDA");

  const showToast = (message, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  const fetchData = useCallback(async () => {
    if (watchlist.length === 0) {
      setStocks([]);
      setOptions([]);
      setCoveredCalls([]);
      setCashSecuredPuts([]);
      setSpreads([]);
      setIronCondors([]);
      setButterflies([]);
      setOptionsChain([]);
      setBacktests([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const symbolsParam = watchlist.join(",");
      
      // Fetch all data in parallel
      const [stocksRes, optionsRes, coveredCallsRes, putsRes, spreadsRes, condorsRes, butterfliesRes, chainRes, backtestRes, alertsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/stocks?symbols=${symbolsParam}`),
        axios.get(`${API}/options?symbols=${symbolsParam}`),
        axios.get(`${API}/covered-calls?symbols=${symbolsParam}`),
        axios.get(`${API}/cash-secured-puts?symbols=${symbolsParam}`),
        axios.get(`${API}/spreads?symbols=${symbolsParam}`),
        axios.get(`${API}/iron-condors?symbols=${symbolsParam}`),
        axios.get(`${API}/butterflies?symbols=${symbolsParam}`),
        axios.get(`${API}/options-chain/${chainSymbol}`),
        axios.get(`${API}/backtest?symbols=${symbolsParam}`),
        axios.get(`${API}/alerts?limit=50`),
        axios.get(`${API}/alerts/settings`)
      ]);

      setStocks(stocksRes.data);
      setOptions(optionsRes.data);
      setCoveredCalls(coveredCallsRes.data);
      setCashSecuredPuts(putsRes.data);
      setSpreads(spreadsRes.data);
      setIronCondors(condorsRes.data);
      setButterflies(butterfliesRes.data);
      setOptionsChain(chainRes.data);
      setBacktests(backtestRes.data);
      setAlerts(alertsRes.data);
      setAlertSettings(settingsRes.data);
      setLastRefresh(new Date());
      
      // Check for new alerts
      const checkRes = await axios.get(`${API}/alerts/check?symbols=${symbolsParam}`);
      if (checkRes.data.count > 0) {
        showToast(`${checkRes.data.count} new high-score alert(s)!`, 'success');
        // Refresh alerts list
        const newAlertsRes = await axios.get(`${API}/alerts?limit=50`);
        setAlerts(newAlertsRes.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      showToast("Error fetching market data", "error");
    } finally {
      setLoading(false);
    }
  }, [watchlist, chainSymbol]);

  // Alert management functions
  const handleMarkAlertRead = async (alertId) => {
    try {
      await axios.post(`${API}/alerts/${alertId}/read`);
      setAlerts(prev => prev.map(a => a.id === alertId ? {...a, read: true} : a));
    } catch (error) {
      showToast("Error marking alert as read", "error");
    }
  };

  const handleMarkAllAlertsRead = async () => {
    try {
      await axios.post(`${API}/alerts/read-all`);
      setAlerts(prev => prev.map(a => ({...a, read: true})));
      showToast("All alerts marked as read");
    } catch (error) {
      showToast("Error marking alerts as read", "error");
    }
  };

  const handleDeleteAlert = async (alertId) => {
    try {
      await axios.delete(`${API}/alerts/${alertId}`);
      setAlerts(prev => prev.filter(a => a.id !== alertId));
    } catch (error) {
      showToast("Error deleting alert", "error");
    }
  };

  const handleClearAllAlerts = async () => {
    try {
      await axios.delete(`${API}/alerts`);
      setAlerts([]);
      showToast("All alerts cleared");
    } catch (error) {
      showToast("Error clearing alerts", "error");
    }
  };

  const handleUpdateAlertSettings = async (newSettings) => {
    try {
      const params = new URLSearchParams();
      Object.entries(newSettings).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value);
        }
      });
      const res = await axios.put(`${API}/alerts/settings?${params.toString()}`);
      setAlertSettings(res.data);
      showToast("Alert settings updated");
    } catch (error) {
      showToast("Error updating settings", "error");
    }
  };

  // Initial fetch and auto-refresh
  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleAddStock = async () => {
    if (!newStock.trim()) return;
    
    const symbol = newStock.trim().toUpperCase();
    
    if (watchlist.includes(symbol)) {
      showToast(`${symbol} is already in your watchlist`, "error");
      return;
    }

    try {
      // Verify stock exists
      await axios.get(`${API}/stocks/${symbol}`);
      setWatchlist(prev => [...prev, symbol]);
      setNewStock("");
      showToast(`Added ${symbol} to watchlist`);
    } catch (error) {
      showToast(`Could not find stock: ${symbol}`, "error");
    }
  };

  const handleRemoveStock = (symbol) => {
    setWatchlist(prev => prev.filter(s => s !== symbol));
    showToast(`Removed ${symbol} from watchlist`);
  };

  const handleRefresh = () => {
    fetchData();
    showToast("Data refreshed");
  };

  return (
    <div className="app-container" data-testid="finance-dashboard">
      {/* Sidebar */}
      <aside className="sidebar" data-testid="sidebar">
        <div className="sidebar-header">
          <h2 className="sidebar-title">
            <Settings size={20} />
            Dashboard Settings
          </h2>
          <p className="sidebar-subtitle">Manage your stock watchlist</p>
        </div>

        <div className="divider"></div>

        {/* Add Stock */}
        <div className="sidebar-section">
          <h3 className="sidebar-section-title">Add Stock</h3>
          <div className="stock-input-group">
            <input
              type="text"
              className="stock-input"
              placeholder="e.g., AAPL"
              value={newStock}
              onChange={(e) => setNewStock(e.target.value.toUpperCase())}
              onKeyPress={(e) => e.key === 'Enter' && handleAddStock()}
              data-testid="add-stock-input"
            />
            <button 
              className="btn btn-primary" 
              onClick={handleAddStock}
              data-testid="add-stock-btn"
            >
              <Plus size={16} />
            </button>
          </div>
        </div>

        <div className="divider"></div>

        {/* Remove Stock */}
        <div className="sidebar-section">
          <h3 className="sidebar-section-title">Remove Stock</h3>
          {watchlist.length > 0 && (
            <>
              <select
                className="stock-select"
                value={selectedRemove}
                onChange={(e) => setSelectedRemove(e.target.value)}
                data-testid="remove-stock-select"
              >
                <option value="">Select stock to remove</option>
                {watchlist.map(symbol => (
                  <option key={symbol} value={symbol}>{symbol}</option>
                ))}
              </select>
              <button 
                className="btn btn-danger btn-full" 
                style={{ marginTop: '12px' }}
                onClick={() => {
                  if (selectedRemove) {
                    handleRemoveStock(selectedRemove);
                    setSelectedRemove("");
                  }
                }}
                disabled={!selectedRemove}
                data-testid="remove-stock-btn"
              >
                Remove Stock
              </button>
            </>
          )}
        </div>

        <div className="divider"></div>

        {/* Current Watchlist */}
        <div className="sidebar-section">
          <h3 className="sidebar-section-title">Current Watchlist</h3>
          <div className="watchlist-tags">
            {watchlist.map(symbol => (
              <span key={symbol} className="stock-tag">
                {symbol}
                <X 
                  size={12} 
                  className="stock-tag-remove" 
                  onClick={() => handleRemoveStock(symbol)}
                />
              </span>
            ))}
          </div>
        </div>

        <div className="divider"></div>

        {/* Refresh Button */}
        <button 
          className="btn btn-secondary btn-full" 
          onClick={handleRefresh}
          data-testid="refresh-btn"
        >
          <RefreshCw size={16} style={{ marginRight: '8px' }} />
          Refresh Data
        </button>

        {/* Last Refresh */}
        <div className="refresh-indicator" style={{ marginTop: '16px' }}>
          <p>Last updated</p>
          <small>{lastRefresh.toLocaleTimeString()}</small>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        {/* Header */}
        <header className="main-header" data-testid="main-header">
          <div className="header-content">
            <div>
              <h1>Finance Dashboard</h1>
              <p>Real-time stock tracking and options analysis • Auto-refresh every 5 minutes</p>
            </div>
            <button 
              className={`alerts-toggle-btn ${alerts.filter(a => !a.read).length > 0 ? 'has-alerts' : ''}`}
              onClick={() => setShowAlertsPanel(!showAlertsPanel)}
              data-testid="alerts-toggle"
            >
              {alerts.filter(a => !a.read).length > 0 ? <BellRing size={20} /> : <Bell size={20} />}
              {alerts.filter(a => !a.read).length > 0 && (
                <span className="alerts-count">{alerts.filter(a => !a.read).length}</span>
              )}
            </button>
          </div>
        </header>

        {/* Alerts Panel Overlay */}
        {showAlertsPanel && (
          <div className="alerts-overlay" onClick={() => setShowAlertsPanel(false)}>
            <div className="alerts-panel-wrapper" onClick={(e) => e.stopPropagation()}>
              <AlertsPanel 
                alerts={alerts}
                onMarkRead={handleMarkAlertRead}
                onMarkAllRead={handleMarkAllAlertsRead}
                onDelete={handleDeleteAlert}
                onClearAll={handleClearAllAlerts}
                settings={alertSettings}
                onUpdateSettings={handleUpdateAlertSettings}
              />
            </div>
          </div>
        )}

        {loading ? (
          <Loading />
        ) : (
          <>
            {/* Stocks Section */}
            <section>
              <div className="section-header">
                <Activity className="section-icon" size={24} style={{ color: '#6366f1' }} />
                <h2>Stock Overview</h2>
              </div>
              
              {stocks.length > 0 ? (
                <div className="stocks-grid" data-testid="stocks-grid">
                  {stocks.map(stock => (
                    <StockCard key={stock.symbol} stock={stock} />
                  ))}
                </div>
              ) : (
                <div className="error-card">
                  <p>No stock data available. Please add stocks to your watchlist.</p>
                </div>
              )}
            </section>

            {/* Options Section */}
            <section>
              <div className="options-header" data-testid="options-header">
                <h2>
                  <BarChart3 size={24} style={{ display: 'inline', marginRight: '12px', verticalAlign: 'middle' }} />
                  Call Options Analysis
                </h2>
                <p>Nearest expiration calls with strike price 10% above current market price</p>
              </div>
              
              <OptionsTable options={options} />
              <OptionsSummary stocks={stocks} options={options} />
            </section>

            {/* Covered Call Screener Section */}
            <section>
              <div className="screener-header" data-testid="screener-header">
                <h2>
                  <Target size={24} style={{ display: 'inline', marginRight: '12px', verticalAlign: 'middle' }} />
                  Covered Call Screener
                </h2>
                <p>Best premium opportunities ranked by yield, annualized return, and upside potential</p>
              </div>
              
              <CoveredCallScreener opportunities={coveredCalls} />
            </section>

            {/* Cash Secured Puts Section */}
            <section>
              <div className="puts-header" data-testid="puts-header">
                <h2>
                  <Shield size={24} style={{ display: 'inline', marginRight: '12px', verticalAlign: 'middle' }} />
                  Cash-Secured Puts Screener
                </h2>
                <p>Get paid to buy stocks at a discount - ranked by premium and downside protection</p>
              </div>
              
              <CashSecuredPutsScreener opportunities={cashSecuredPuts} />
            </section>

            {/* Spreads Section */}
            <section>
              <div className="spreads-header" data-testid="spreads-header">
                <h2>
                  <GitBranch size={24} style={{ display: 'inline', marginRight: '12px', verticalAlign: 'middle' }} />
                  Vertical Spreads Screener
                </h2>
                <p>Bull call, bear put, and credit spread strategies - defined risk trading</p>
              </div>
              
              <SpreadsScreener spreads={spreads} />
            </section>

            {/* Iron Condors Section */}
            <section>
              <div className="condor-header" data-testid="condor-header">
                <h2>
                  <Layers size={24} style={{ display: 'inline', marginRight: '12px', verticalAlign: 'middle' }} />
                  Iron Condor Screener
                </h2>
                <p>Profit from low volatility - collect premium when stock stays in range</p>
              </div>
              
              <IronCondorScreener condors={ironCondors} />
            </section>

            {/* Butterfly Spreads Section */}
            <section>
              <div className="butterfly-header" data-testid="butterfly-header">
                <h2>
                  <Zap size={24} style={{ display: 'inline', marginRight: '12px', verticalAlign: 'middle' }} />
                  Butterfly Spread Screener
                </h2>
                <p>High reward-to-risk plays when expecting stock to stay near a specific price</p>
              </div>
              
              <ButterflyScreener butterflies={butterflies} />
            </section>

            {/* Options Chain Section */}
            <section>
              <div className="chain-section-header" data-testid="chain-header">
                <h2>
                  <Grid3X3 size={24} style={{ display: 'inline', marginRight: '12px', verticalAlign: 'middle' }} />
                  Options Chain with Greeks
                </h2>
                <p>Full options chain with Delta, Gamma, Theta, Vega for analysis</p>
                <div className="chain-symbol-selector">
                  <select 
                    value={chainSymbol} 
                    onChange={(e) => setChainSymbol(e.target.value)}
                    className="stock-select"
                  >
                    {watchlist.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              
              <OptionsChainView chain={optionsChain} selectedSymbol={chainSymbol} />
            </section>

            {/* Backtesting Section */}
            <section>
              <div className="backtest-header" data-testid="backtest-header">
                <h2>
                  <History size={24} style={{ display: 'inline', marginRight: '12px', verticalAlign: 'middle' }} />
                  Strategy Backtesting
                </h2>
                <p>Historical P&L simulation for covered call strategy (6 months)</p>
              </div>
              
              <BacktestResults backtests={backtests} />
            </section>
          </>
        )}

        {/* Footer */}
        <footer className="footer">
          <p>Data provided by Yahoo Finance • Prices may be delayed</p>
        </footer>
      </main>

      {/* Toast Container */}
      <div className="toast-container">
        {toasts.map(toast => (
          <Toast 
            key={toast.id} 
            message={toast.message} 
            type={toast.type}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>
    </div>
  );
}

export default App;
